import * as React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Wallet, ArrowLeft, PlayCircle, ShieldAlert, ShieldCheck, AlertTriangle,
  Info, Circle, Clock, Lock, RefreshCw, CheckCircle2, CalendarDays,
  ChevronRight, FileText, Users, Coins, RotateCcw, ClipboardCheck, Receipt,
} from "lucide-react";
import { api, formatApiErrorDetail, BACKEND } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { SkeletonTable } from "@/components/feel/Skeleton";
import { EmptyState } from "@/components/feel/EmptyState";

const STATUS_ORDER = ["open", "collecting", "draft", "in_review", "approved", "committed", "closed"];
const STATUS_LABEL = {
  open: "Open", collecting: "Collecting", draft: "Draft",
  in_review: "In review", approved: "Approved",
  committed: "Committed", closed: "Closed",
};

function fmtGBP(pence) {
  if (pence == null) return "—";
  return "£" + (pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusChip({ s, size = "sm" }) {
  const map = {
    open:       "chip",
    collecting: "chip chip-violet",
    draft:      "chip chip-violet",
    in_review:  "chip chip-amber",
    approved:   "chip chip-emerald",
    committed:  "chip chip-emerald",
    closed:     "chip",
  };
  const px = size === "lg" ? "px-2.5 py-1 text-[11px]" : "text-[10px]";
  return (
    <span className={`${map[s] || "chip"} mono-num ${px}`} data-testid={`status-chip-${s}`}>
      {STATUS_LABEL[s] || s}
    </span>
  );
}

function SeverityIcon({ severity, blocking }) {
  if (blocking) return <ShieldAlert size={13} className="text-brand-coral" />;
  if (severity === "error") return <AlertTriangle size={13} className="text-brand-coral" />;
  if (severity === "warn") return <AlertTriangle size={13} className="text-brand-amber" />;
  return <Info size={13} className="text-brand-muted" />;
}

// ─────────────────────────────────────────────────────────────
// LIST — /c/:companyId/m/pay/v2
// ─────────────────────────────────────────────────────────────

function PeriodsList({ companyId }) {
  const [rows, setRows] = React.useState(null);
  const [filter, setFilter] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/companies/${companyId}/pay/v2/periods`);
      setRows(data.periods);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  const visible = React.useMemo(() => {
    if (!rows) return null;
    if (!filter) return rows;
    return rows.filter(r => r.status === filter);
  }, [rows, filter]);

  if (!rows) return (
    <div className="card-panel overflow-hidden" data-testid="pay-v2-loading">
      <SkeletonTable rows={5} cols={6} />
    </div>
  );

  return (
    <div data-testid="pay-v2-periods-list">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[12px] text-brand-muted">
          <span className="mono-num text-brand-ink">{rows.length}</span> period{rows.length === 1 ? "" : "s"}
          {" · "}<span className="text-brand-muted">Exceptions before totals — resolve before approving.</span>
        </div>
        <div className="flex items-center gap-1">
          <select
            className="field-input !h-7 !py-0 text-[11.5px]"
            value={filter} onChange={e => setFilter(e.target.value)}
            data-testid="pay-v2-status-filter"
          >
            <option value="">All statuses</option>
            {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </select>
        </div>
      </div>

      <div className="card-panel overflow-hidden">
        <div className="table-scroll">
          <table className="fable-table" data-testid="pay-v2-periods-table">
            <thead>
              <tr>
                <th style={{ width: "24%" }}>Period</th>
                <th style={{ width: "12%" }}>Pay date</th>
                <th style={{ width: "12%" }}>Status</th>
                <th style={{ width: "18%" }} className="text-right">Exceptions</th>
                <th style={{ width: "12%" }} className="text-right">Blocking</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={6} className="p-0">
                  <EmptyState icon={CalendarDays} eyebrow="Periods"
                    title="No pay periods yet."
                    body="Seed a pay group + periods via the backend script to see them here."
                    testid="pay-v2-empty" />
                </td></tr>
              )}
              {visible.map(p => (
                <tr key={p.id} data-testid={`pay-v2-period-row-${p.id}`}
                    data-clickable="true">
                  <td className="mono-num text-[12px] text-brand-ink">
                    <Link to={`/c/${companyId}/m/pay/v2/${p.id}`}
                          className="hover:text-brand-violet"
                          data-testid={`pay-v2-period-link-${p.id}`}>
                      {p.period_start} → {p.period_end}
                    </Link>
                  </td>
                  <td className="mono-num text-[12px] text-brand-muted">{p.pay_date}</td>
                  <td><StatusChip s={p.status} /></td>
                  <td className="mono-num text-right text-brand-ink">
                    {p.exception_summary?.total || 0}
                  </td>
                  <td className="mono-num text-right">
                    {(p.exception_summary?.blocking || 0) > 0 ? (
                      <span className="chip chip-coral mono-num text-[10px]"
                            data-testid={`pay-v2-blocking-${p.id}`}>
                        <ShieldAlert size={10} /> {p.exception_summary.blocking}
                      </span>
                    ) : (
                      <span className="text-brand-muted">0</span>
                    )}
                  </td>
                  <td className="text-right">
                    <Link to={`/c/${companyId}/m/pay/v2/${p.id}`}
                          className="btn-ghost !h-7 !px-2 inline-flex items-center gap-1"
                          data-testid={`pay-v2-open-${p.id}`}>
                      Open <ChevronRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DETAIL — /c/:companyId/m/pay/v2/:periodId
// Exceptions-first: queue on top, totals collapsed below.
// ─────────────────────────────────────────────────────────────

function PeriodDetail({ companyId, periodId }) {
  const nav = useNavigate();
  const { has } = useCompany();
  const canManage = has("pay.manage");
  const canApprove = has("pay.approve");
  const [period, setPeriod] = React.useState(null);
  const [exceptions, setExceptions] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [running, setRunning] = React.useState(false);
  const [showTotals, setShowTotals] = React.useState(false);
  const [snapshot, setSnapshot] = React.useState(null);
  const [adjSummary, setAdjSummary] = React.useState(null);
  const [retroLines, setRetroLines] = React.useState(null);
  const [approval, setApproval] = React.useState(null);
  const [payslips, setPayslips] = React.useState(null);
  const [detectingRetro, setDetectingRetro] = React.useState(false);
  const [castingApproval, setCastingApproval] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      const [p, e, ev, adj, retro, appr, slips] = await Promise.all([
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/exceptions?unresolved_only=false`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/events`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/adjustments/summary`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/retro-lines`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/approval-status`),
        api.get(`/companies/${companyId}/pay/v2/periods/${periodId}/payslips`),
      ]);
      setPeriod(p.data);
      setExceptions(e.data.exceptions);
      setEvents(ev.data.events);
      setAdjSummary(adj.data);
      setRetroLines(retro.data);
      setApproval(appr.data);
      setPayslips(slips.data.payslips);
      if (p.data.snapshot_id) {
        const r = await api.post(`/companies/${companyId}/pay/v2/periods/${periodId}/reproduce`);
        setSnapshot(r.data);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, [companyId, periodId]);
  React.useEffect(() => { load(); }, [load]);

  async function runChecks() {
    setRunning(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/pay/v2/periods/${periodId}/run-checks`);
      toast.success(`Ran ${data.checks_run} checks · ${data.total} exceptions (${data.blocking} blocking)`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRunning(false);
    }
  }

  async function resolveOne(exc) {
    const note = prompt(
      `Resolve "${exc.title}"?\n\nEnter a resolution note (min 2 chars):`,
      exc.suggested_resolution || "",
    );
    if (!note || note.trim().length < 2) return;
    try {
      await api.post(`/companies/${companyId}/pay/v2/exceptions/${exc.id}/resolve`, {
        resolution_note: note.trim(),
      });
      toast.success("Exception resolved");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }

  async function runRetroDetect() {
    setDetectingRetro(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/pay/v2/periods/${periodId}/retro/detect`);
      toast.success(`Detected ${data.detected} retro line${data.detected === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setDetectingRetro(false); }
  }

  async function castApproval() {
    const note = prompt("Add a note for your approval (optional):", "");
    setCastingApproval(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/pay/v2/periods/${periodId}/approvals`, {
        note: note || null,
      });
      toast.success(`Approval ${data.given}/${data.required} recorded${data.complete ? " — chain complete" : ""}`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "sod_violation") {
        toast.error(`SoD block: ${detail.message || detail.reason}`);
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally { setCastingApproval(false); }
  }

  if (!period || !exceptions) return (
    <div className="px-4 md:px-6 py-4">
      <div className="card-panel overflow-hidden" data-testid="pay-v2-detail-loading">
        <SkeletonTable rows={6} cols={5} />
      </div>
    </div>
  );

  const unresolved = exceptions.filter(x => !x.resolved);
  const resolved   = exceptions.filter(x =>  x.resolved);
  const blocking   = unresolved.filter(x => x.blocking);
  const bySeverity = unresolved.reduce((acc, x) => {
    (acc[x.severity] ??= []).push(x); return acc;
  }, {});
  const locked = period.status === "committed" || period.status === "closed";

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="pay-v2-detail">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <button className="btn-ghost !h-7 !px-2 inline-flex items-center gap-1 text-[12px]"
                onClick={() => nav(`/c/${companyId}/m/pay?tab=periods`)}
                data-testid="pay-v2-back">
          <ArrowLeft size={13} /> Periods
        </button>
        <span className="text-brand-muted">/</span>
        <span className="mono-num text-[12px] text-brand-ink">
          {period.period_start} → {period.period_end}
        </span>
      </div>

      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
              <Wallet size={17} />
            </div>
            <div>
              <div className="text-[19px] font-bold tracking-tight text-brand-ink">
                Pay period
              </div>
              <div className="text-[11.5px] text-brand-muted flex items-center gap-2">
                <CalendarDays size={11} /> pay date <span className="mono-num">{period.pay_date}</span>
                {period.pay_date_reason && (
                  <span className="text-brand-amber text-[10.5px]">· {period.pay_date_reason}</span>
                )}
                {period.off_cycle && (
                  <span className="chip chip-amber text-[10px] mono-num" data-testid="off-cycle-chip">
                    off-cycle
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusChip s={period.status} size="lg" />
          {canManage && !locked && (
            <button className="btn-violet" onClick={runChecks} disabled={running}
                    data-testid="pay-v2-run-checks">
              <RefreshCw size={13} className={running ? "animate-spin" : ""} />
              {running ? "Running…" : "Run checks"}
            </button>
          )}
        </div>
      </div>

      {/* Compliance banner */}
      {blocking.length > 0 && (
        <div className="card-panel mb-4 border-brand-coral/40"
             style={{ background: "rgba(239,108,108,0.06)" }}
             data-testid="pay-v2-blocking-banner">
          <div className="card-panel-body flex items-start gap-3">
            <ShieldAlert size={18} className="text-brand-coral shrink-0 mt-0.5" />
            <div>
              <div className="text-[13px] font-semibold text-brand-ink">
                {blocking.length} blocking exception{blocking.length === 1 ? "" : "s"} — approval is blocked.
              </div>
              <div className="text-[12px] text-brand-muted">
                Each of these must be resolved before this period can move to approved.
              </div>
            </div>
          </div>
        </div>
      )}
      {blocking.length === 0 && unresolved.length === 0 && !locked && (
        <div className="card-panel mb-4 border-brand-emerald/40"
             style={{ background: "rgba(16,185,129,0.06)" }}
             data-testid="pay-v2-clean-banner">
          <div className="card-panel-body flex items-center gap-3">
            <ShieldCheck size={16} className="text-brand-emerald" />
            <div className="text-[12.5px] text-brand-ink">
              No open exceptions. Period is ready to progress through the approval flow.
            </div>
          </div>
        </div>
      )}

      {/* ── EXCEPTIONS QUEUE — top-of-page. */}
      <section className="mb-6" data-testid="pay-v2-exceptions-section">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <div className="text-[13px] font-semibold text-brand-ink">Exception queue</div>
            <span className="mono-num text-[11px] text-brand-muted">
              {unresolved.length} open · {resolved.length} resolved
            </span>
          </div>
        </div>

        {unresolved.length === 0 && exceptions.length === 0 && (
          <EmptyState icon={ShieldCheck} eyebrow="Checks"
            title={locked ? "Committed — checks archived." : "No exceptions detected."}
            body={locked
              ? "This period is frozen. Its snapshot below is the source of truth."
              : "Run checks to see what stands between this period and approval."}
            testid="pay-v2-exceptions-empty" />
        )}

        {["error", "warn", "info"].map(sev => {
          const rows = bySeverity[sev] || [];
          if (rows.length === 0) return null;
          const groupLabel = { error: "Errors", warn: "Warnings", info: "Info" }[sev];
          return (
            <div key={sev} className="card-panel mb-3 overflow-hidden"
                 data-testid={`pay-v2-exceptions-${sev}`}>
              <div className="px-3 py-2 border-b border-brand-rule flex items-center gap-2">
                <SeverityIcon severity={sev} />
                <span className="text-[12px] font-semibold text-brand-ink">{groupLabel}</span>
                <span className="mono-num text-[11px] text-brand-muted">{rows.length}</span>
              </div>
              <ul className="divide-y divide-brand-rule">
                {rows.map(x => (
                  <li key={x.id} className="p-3 hover:bg-accent transition-colors"
                      data-testid={`pay-v2-exception-${x.code}-${x.id}`}>
                    <div className="flex items-start gap-3">
                      <SeverityIcon severity={x.severity} blocking={x.blocking} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[12.5px] font-semibold text-brand-ink">{x.title}</span>
                          {x.blocking && (
                            <span className="chip chip-coral mono-num text-[10px]"
                                  data-testid={`pay-v2-blocking-pill-${x.id}`}>
                              <Lock size={9} /> blocks approval
                            </span>
                          )}
                          <span className="chip text-[10px] mono-num">{x.code}</span>
                        </div>
                        <div className="mt-1 text-[11.5px] text-brand-muted">{x.detail}</div>
                        {x.suggested_resolution && (
                          <div className="mt-1 text-[11px] text-brand-muted italic">
                            → {x.suggested_resolution}
                          </div>
                        )}
                      </div>
                      {canManage && !locked && (
                        <button className="btn-ghost !h-7 !px-2 text-[11.5px]"
                                onClick={() => resolveOne(x)}
                                data-testid={`pay-v2-resolve-${x.id}`}>
                          <CheckCircle2 size={12} /> Resolve
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}

        {resolved.length > 0 && (
          <details className="card-panel px-3 py-2" data-testid="pay-v2-resolved-details">
            <summary className="text-[12px] text-brand-muted cursor-pointer">
              Resolved ({resolved.length})
            </summary>
            <ul className="mt-2 space-y-1.5">
              {resolved.map(x => (
                <li key={x.id} className="text-[11.5px] text-brand-muted flex items-center gap-2"
                    data-testid={`pay-v2-resolved-${x.id}`}>
                  <CheckCircle2 size={11} className="text-brand-emerald" />
                  <span className="mono-num text-[10px]">{x.code}</span>
                  <span className="text-brand-ink">{x.title}</span>
                  {x.resolution_note && <span className="italic">— {x.resolution_note}</span>}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>

      {/* ── PAY-V2 ACTIVITY GRID — adjustments · retros · approvals · payslips */}
      <section className="mb-6" data-testid="pay-v2-activity-section">
        <div className="text-[13px] font-semibold text-brand-ink mb-2">Pay v2 activity</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <ActivityTile
            icon={Coins} label="Adjustments"
            value={adjSummary ? fmtGBP(adjSummary.net_impact_cents) : "—"}
            hint={adjSummary
              ? `${adjSummary.row_count} row${adjSummary.row_count === 1 ? "" : "s"} · net impact`
              : "no rows yet"}
            testid="pay-v2-tile-adjustments"
            href={`${BACKEND}/api/companies/${companyId}/pay/v2/periods/${periodId}/handoff.json`}
            hrefLabel="handoff.json"
          />
          <ActivityTile
            icon={RotateCcw} label="Retro lines"
            value={retroLines ? retroLines.count : "—"}
            hint={retroLines
              ? `${retroLines.total_pending_cents ? `pending ${fmtGBP(retroLines.total_pending_cents)}` : "all posted"}`
              : ""}
            testid="pay-v2-tile-retros"
            action={canManage && !locked ? {
              label: detectingRetro ? "Detecting…" : "Detect retros",
              onClick: runRetroDetect,
              disabled: detectingRetro,
            } : null}
          />
          <ActivityTile
            icon={ClipboardCheck} label="Approvals"
            value={approval
              ? `${approval.approvers_signed}/${approval.required_approvers}`
              : "—"}
            hint={approval
              ? approval.complete
                ? "chain complete"
                : `est. gross ${fmtGBP(approval.estimated_gross_cents)}`
              : ""}
            testid="pay-v2-tile-approvals"
            action={canApprove && approval && !approval.complete && period.status === "in_review"
              ? {
                  label: castingApproval ? "Signing…" : "Sign approval",
                  onClick: castApproval,
                  disabled: castingApproval || !approval.caller_can_approve,
                  disabledReason: approval.caller_sod_block ? `SoD: ${approval.caller_sod_block}` : null,
                } : null}
          />
          <ActivityTile
            icon={Receipt} label="Payslips"
            value={payslips ? payslips.length : "—"}
            hint={payslips && payslips.length
              ? `total ${fmtGBP(payslips.reduce((a, s) => a + s.gross_pay_cents, 0))}`
              : "generated on commit"}
            testid="pay-v2-tile-payslips"
            href={payslips && payslips.length
              ? `${BACKEND}/api/companies/${companyId}/pay/v2/periods/${periodId}/handoff.csv`
              : null}
            hrefLabel="handoff.csv"
          />
        </div>
      </section>

      {/* ── TOTALS (collapsed) */}
      <section className="mb-6" data-testid="pay-v2-totals-section">
        <button className="w-full flex items-center justify-between card-panel px-3 py-2 hover:bg-accent transition-colors"
                onClick={() => setShowTotals(v => !v)}
                data-testid="pay-v2-totals-toggle">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-brand-muted" />
            <span className="text-[12.5px] font-semibold text-brand-ink">Totals</span>
            <span className="text-[11px] text-brand-muted">
              {snapshot ? "· from committed snapshot" : "· preview — commit to freeze"}
            </span>
          </div>
          <ChevronRight size={14}
                        className={"text-brand-muted transition-transform " + (showTotals ? "rotate-90" : "")} />
        </button>
        {showTotals && (
          <div className="mt-2 card-panel">
            <div className="card-panel-body">
              {snapshot ? (
                <div className="grid grid-cols-3 gap-3">
                  <StatTile label="Total gross" value={fmtGBP(snapshot.recomputed.total_gross_cents)} highlight />
                  <StatTile label="Total minutes" value={snapshot.recomputed.total_worked_minutes.toLocaleString()} />
                  <StatTile label="People" value={snapshot.recomputed.person_count} />
                  <div className="col-span-3 text-[11px] text-brand-muted flex items-center gap-1.5">
                    <ShieldCheck size={12} className="text-brand-emerald" />
                    Reproduce match:{" "}
                    <span className={snapshot.match ? "text-brand-emerald mono-num" : "text-brand-coral mono-num"}
                          data-testid="pay-v2-snapshot-match">
                      {snapshot.match ? "true" : "false"}
                    </span>
                    <span>· snapshot id <span className="mono-num text-brand-ink">{snapshot.snapshot_id.slice(0, 8)}</span></span>
                  </div>
                </div>
              ) : (
                <div className="text-[12px] text-brand-muted flex items-center gap-2">
                  <Info size={13} />
                  Totals become authoritative once this period is committed. Until then, they're derived live from
                  timesheets and can shift with every exception resolved or reshaped shift.
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── TIMELINE */}
      <section data-testid="pay-v2-timeline-section">
        <div className="text-[13px] font-semibold text-brand-ink mb-2">Status timeline</div>
        <div className="card-panel px-3 py-3">
          {events.length === 0 ? (
            <div className="text-[11.5px] text-brand-muted">No transitions yet.</div>
          ) : (
            <ol className="space-y-2">
              {events.map(ev => (
                <li key={ev.id} className="flex items-center gap-3 text-[11.5px]"
                    data-testid={`pay-v2-event-${ev.id}`}>
                  <Circle size={7} className="text-brand-violet fill-current shrink-0" />
                  <span className="mono-num text-brand-muted">{ev.ts.slice(0, 16).replace("T", " ")}</span>
                  <span className="text-brand-ink">
                    {STATUS_LABEL[ev.from_status] || ev.from_status} → {STATUS_LABEL[ev.to_status] || ev.to_status}
                  </span>
                  <span className="text-brand-muted">· {ev.actor_name || ev.actor_id?.slice(0, 8)}</span>
                  {ev.note && <span className="text-brand-muted italic">"{ev.note}"</span>}
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      <div className="mt-6 text-[10.5px] text-brand-muted flex items-center gap-1.5">
        <Info size={11} />
        Pay v2 activity below the exception queue: sign approvals · detect retros · view payslips. Adjustments &amp; bulk / reversal actions available via API in 16.2.
      </div>
    </div>
  );
}

function StatTile({ label, value, highlight }) {
  return (
    <div className={`card-panel px-4 py-3 ${highlight ? "border-brand-violet/25 shadow-card" : ""}`}>
      <div className="micro-label">{label}</div>
      <div className={`mt-1 mono-num text-[22px] font-bold ${highlight ? "text-brand-violet" : "text-brand-ink"}`}>
        {value}
      </div>
    </div>
  );
}

function ActivityTile({ icon: Icon, label, value, hint, testid, action, href, hrefLabel }) {
  return (
    <div className="card-panel px-4 py-3 flex flex-col gap-1" data-testid={testid}>
      <div className="flex items-center gap-2 micro-label">
        <Icon size={12} className="text-brand-muted" />
        {label}
      </div>
      <div className="mono-num text-[20px] font-bold text-brand-ink" data-testid={`${testid}-value`}>
        {value}
      </div>
      {hint && <div className="text-[10.5px] text-brand-muted">{hint}</div>}
      {action && (
        <button
          className="btn-ghost !h-7 !px-2 mt-1 text-[11px] self-start"
          onClick={action.onClick} disabled={action.disabled}
          title={action.disabledReason || undefined}
          data-testid={`${testid}-action`}
        >
          {action.label}
        </button>
      )}
      {href && (
        <a href={href} target="_blank" rel="noreferrer"
           className="text-[10.5px] text-brand-violet mt-1 self-start hover:underline"
           data-testid={`${testid}-download`}>
          ↓ {hrefLabel}
        </a>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Route entry — decides list vs detail from useParams.periodId
// ─────────────────────────────────────────────────────────────

export default function PayV2() {
  const { companyId, periodId } = useParams();
  if (periodId) return <PeriodDetail companyId={companyId} periodId={periodId} />;
  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1200px]" data-testid="pay-v2-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-md bg-accent text-brand-violet flex items-center justify-center">
          <Wallet size={17} />
        </div>
        <div>
          <div className="text-[19px] font-bold tracking-tight text-brand-ink">Pay periods</div>
          <div className="text-[11.5px] text-brand-muted">
            Pay v2 · period calendar → checks → snapshot. Read-only in 16.1 · approval + commit ship in 16.2.
          </div>
        </div>
      </div>
      <div className="mb-4 flex items-center gap-2">
        <Link to={`/c/${companyId}/m/pay`} className="btn-ghost !h-7 !px-2 text-[12px] inline-flex items-center gap-1"
              data-testid="pay-v2-to-runs">
          <ArrowLeft size={12} /> Runs (legacy)
        </Link>
      </div>
      <PeriodsList companyId={companyId} />
    </div>
  );
}
